import { AlertTriangle, Info } from 'lucide-react';

import { Button, Separator, SharedSpinner } from '@codes/ui-kit';

interface DangerZoneProps {
    title: string;
    warning: string;
    entityName: string;
    entityInfo?: string | React.ReactNode;
    onDelete: () => void;
    deleteButtonText: string;
    isDeleting?: boolean;
    isDeleteDisabled?: boolean;
    disabledReason?: string;
}

export const DangerZone = ({
    title,
    warning,
    entityName,
    entityInfo,
    onDelete,
    deleteButtonText,
    isDeleting,
    isDeleteDisabled,
    disabledReason,
}: DangerZoneProps) => {
    return (
        <div className="border-2 border-error/20 rounded-xl md:rounded-2xl p-4 md:p-6 bg-error/5">
            <div className="flex items-start gap-3 mb-4">
                <div className="w-8 h-8 rounded-full bg-error/10 flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="w-4 h-4 text-error" />
                </div>
                <div className="flex-1 min-w-0">
                    <h2 className="text-base md:text-lg font-semibold text-gray-900 dark:text-white mb-1">{title}</h2>
                    <p className="text-xs md:text-sm text-error font-medium">{warning}</p>
                </div>
            </div>

            <Separator className="my-4" />

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 md:p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700/50">
                <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm md:text-base text-gray-900 dark:text-white mb-1 truncate">
                        {entityName}
                    </div>
                    {entityInfo && (
                        <div className="text-xs md:text-sm text-gray-600 dark:text-gray-400">{entityInfo}</div>
                    )}
                </div>
                <Button
                    variant="destructive"
                    size="sm"
                    onClick={onDelete}
                    disabled={isDeleteDisabled || isDeleting}
                    className="w-full sm:w-auto"
                >
                    {isDeleting ? <SharedSpinner className="w-4 h-4" /> : deleteButtonText}
                </Button>
            </div>

            {isDeleteDisabled && disabledReason && (
                <div className="flex items-start gap-2 mt-3 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700/50">
                    <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-gray-500 dark:text-gray-400" />
                    <span className="text-xs md:text-sm text-gray-600 dark:text-gray-400">{disabledReason}</span>
                </div>
            )}
        </div>
    );
};
